import { faSquarePen, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useRef, useState, useMemo, useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useTable } from 'react-table'
import { FormatMoney } from '../../Utils/functions'
import { VAPermissions } from 'Utils/constants'
import PrecioProducto from '../PrecioProducto'
import './SummaryOrderCart.css'
import { QuantityColumn } from './components/QuantityColumn'
import { useTableData } from './hooks/useTableData'
import { removeFromCart } from 'redux/actions/cartActions'
import useValidatePermission from 'Hooks/useValidatePermission'
import CostMarginVisibility from 'Components/CostMarginVisibility/CostMarginVisibility'
import NoImage from '../../Assets/Images/Logos/no-image.png'
import DescriptionCell from './components/DescriptionCell'

function SummaryOrderCart({ isShoppingCart = false }) {
  const productsRef = useRef(null)
  const dispatch = useDispatch()

  // Memorizar columnas a omitir
  const columnsToOmit = useMemo(() => {
    return isShoppingCart ? ['costo'] : ['imagen']
  }, [isShoppingCart])

  const { columns } = useTableData({
    omitColumns: columnsToOmit,
    isShoppingCart,
  })

  // Selectores Redux
  const isCustomerUser = useSelector(state => state.login.isCustomer)
  const products = useSelector(state => state.cart.cart)

  // Permisos
  const seeMargenProd = useValidatePermission(VAPermissions.seeMargenProd)
  const seeCostProd = useValidatePermission(VAPermissions.seeCostProd)

  // Estados locales
  const [showModalPrecio, setShowModalPrecio] = useState(false)
  const [productSelected, setProductSelected] = useState({})
  const [showModalSuccessUpdatePrecio, setShowModalSuccessUpdatePrecio] =
    useState(false)

  // Calcular borde derecho de la tabla
  const rightBorderTable = useMemo(() => {
    if (seeCostProd && seeMargenProd) {
      return 7
    } else if (
      (!seeCostProd && seeMargenProd) ||
      (seeCostProd && !seeMargenProd)
    ) {
      return 6
    } else {
      return 5
    }
  }, [seeCostProd, seeMargenProd])

  const formatData = useMemo(() => {
    return products?.map(product => {
      const productImage =
        (product.Images && product.Images[0]?.URL) ||
        product.DefaultProductPicture ||
        product.productImage ||
        NoImage

      return {
        ...product,
        key: product?.ProductId,
        codigo: product?.Sku,
        descripcion: product?.ProductName,
        imagen: productImage,
        cantidad: product?.Quantity ? product?.Quantity : 1,
        precio: product?.FinalPrice || product?.precio,
        margen: product?.Margin ? product?.Margin.toFixed(2) : 0,
        total: product?.Amount,
      }
    })
  }, [products])

  const tableInstance = useTable({
    columns,
    data: formatData,
    getRowId: row => row.ProductId || row.key,
  })

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    footerGroups,
  } = tableInstance

  const handleModalEditPrice = useCallback(product => {
    setShowModalPrecio(prev => !prev)
    if (product !== undefined) {
      setProductSelected(product)
    }
  }, [])

  const removeToCart = useCallback(
    product => {
      // Asegurarse de que el producto se elimine por completo
      dispatch(
        removeFromCart({
          product: {
            ProductId: product.ProductId,
          },
        })
      )
    },
    [dispatch]
  )

  const setProductChangeValidation = useCallback(
    value => {
      const index = products.findIndex(
        item => item.ProductId === productSelected.ProductId
      )
      if (index !== -1) {
        dispatch({
          type: 'IS_PRICE_CHANGED',
          payload: { index, value },
        })
      }
    },
    [dispatch, products, productSelected.ProductId]
  )

  const handleModalSuccessUpdatePrecio = useCallback(() => {
    if (showModalSuccessUpdatePrecio === false) {
      setShowModalSuccessUpdatePrecio(true)
      setTimeout(() => {
        setShowModalSuccessUpdatePrecio(false)
      }, 5000)
    } else {
      setShowModalSuccessUpdatePrecio(false)
    }
  }, [showModalSuccessUpdatePrecio])

  // Función para renderizar celdas de manera optimizada
  const renderCell = useCallback(
    cell => {
      const { column, row, value } = cell

      switch (column.Header) {
        case 'Acciones':
          return (
            <FontAwesomeIcon
              style={{ color: '#F64E60', fontSize: '2rem' }}
              icon={faTrashCan}
              onClick={() => removeToCart(row.original)}
              data-testid={`btn-remove-summary-product-${row.original.ProductId}`}
            />
          )

        case 'Total':
          return `$${FormatMoney(value)}`

        case 'Costo':
          return `$${FormatMoney(row.original.Cost)}`

        case 'Margen':
          return seeMargenProd ? (
            <CostMarginVisibility
              seeMargenProd={seeMargenProd}
              simple
              isMargin
              margin={value}
            />
          ) : null

        case 'Cantidad':
          return <QuantityColumn cell={row.original} />

        case 'Precio':
          return (
            <>
              {row.original.Price !== row.original.FinalPrice && (
                <p
                  style={{ textDecoration: 'line-through', color: 'orange' }}
                  data-testid={`text-original-price-summary-${row.original.ProductId}`}>
                  ${row.original.FinalPrice.toFixed(2)}
                </p>
              )}
              ${FormatMoney(row.original.Price)}
              {!isCustomerUser && (
                <FontAwesomeIcon
                  icon={faSquarePen}
                  style={{ fontSize: '18px', marginLeft: '4px' }}
                  onClick={() => handleModalEditPrice(row.original)}
                  data-testid={`btn-edit-price-summary-${row.original.ProductId}`}
                />
              )}
            </>
          )

        case 'Imagen':
          return (
            <img
              src={row.original.imagen}
              alt="product-image"
              style={{ maxWidth: '42px' }}
              data-testid={`img-product-summary-${row.original.ProductId}`}
            />
          )

        case 'Descripción':
          if (isShoppingCart) {
            return (
              <DescriptionCell
                key={`desc-${row.original.ProductId}`}
                description={value}
                productId={row.original.ProductId}
              />
            )
          }
          return row.original.descripcion

        default:
          return cell.render('Cell')
      }
    },
    [handleModalEditPrice, isCustomerUser, removeToCart, seeMargenProd]
  )

  return (
    <div
      className="tableContainer-summaryOrder"
      data-testid="container-summary-order-cart">
      <table
        className="single-product-cart inventory-table products"
        {...getTableProps()}
        data-testid="table-summary-order-cart">
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr
              key={i}
              {...headerGroup.getHeaderGroupProps()}
              data-testid={`table-header-row-summary-${i}`}>
              {headerGroup.headers.map((column, i) => (
                <th
                  key={i}
                  {...column.getHeaderProps()}
                  data-testid={`table-header-summary-${column.Header}`}>
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          {...getTableBodyProps()}
          data-testid="table-body-summary-order-cart">
          {rows.map((row, i) => {
            prepareRow(row)
            return (
              <tr
                key={row.id || i}
                {...row.getRowProps()}
                ref={productsRef}
                data-testid={`table-row-summary-product-${row.original.ProductId}`}>
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    {...cell.getCellProps(
                      cell.column.Header === 'Imagen'
                        ? { style: { maxWidth: '100px', textAlign: 'center' } }
                        : {}
                    )}
                    data-testid={`table-cell-summary-${cell.column.Header}-${row.original.ProductId}`}>
                    {renderCell(cell)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          {footerGroups.map((group, i) => (
            <tr key={i} {...group.getFooterGroupProps()}>
              {group.headers.map((column, index) => (
                <td
                  key={index}
                  style={{
                    border: 'none',
                    borderLeft: index !== 0 && 'none',
                    borderRight: index !== rightBorderTable && 'none',
                  }}
                  {...column.getFooterProps()}>
                  {column.render('Footer')}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>

      <Modal
        show={showModalPrecio}
        backdrop="static"
        className="modal-clase-price-change"
        centered
        data-testid="modal-edit-price-summary">
        <PrecioProducto
          handleModalEditPrice={handleModalEditPrice}
          handleModalSuccesUpdatePrecio={handleModalSuccessUpdatePrecio}
          product={productSelected}
          setNuevoPrecio={() => null}
          setProductChangeValidation={setProductChangeValidation}
        />
      </Modal>
    </div>
  )
}

export default React.memo(SummaryOrderCart)
